import * as PIXI from "pixi.js";
import gsap from "gsap";
import ServiceLocator from "../../ServiceLocator";

import Session, { game_type } from "../../Session";
import RadialSpinner from "./RadialSpinner";


export default class Player extends PIXI.Container
{
    private _id: number = null;
    private _name: string = null;
    private _maxUsernameWidth: number = null;
    private _portraitUrl: string = null;

    private _portrait: PIXI.Sprite = null;
    private _nameText: PIXI.Text = null;
    private _scoreText: PIXI.Text = null;
    private _timeOutBase: PIXI.Sprite = null;
    private _timeOutText: PIXI.Text = null;
    private _cooldownBar: PIXI.Sprite = null;
    private _radialMask: RadialSpinner = null;
    private _activePlayer: PIXI.Sprite = null;

    private _nextPlayer: Player = null;

    private _isBot: boolean = null;
    private _isAway: boolean = null;
    private _isBusted: boolean = null;

    private _score: number = null;
    private _side: player_side_type = null;

    private readonly _kActivePlayerScoreColor: number = 0xd0f400;
    private readonly _kPortraitWidth: number = 90;

    
    public set id( value: number ) { this._id = value; }
    public set name( value: string ) { this._name = value; }
    public set maxUsernameWidth( value: number ) { this._maxUsernameWidth = value; }
    public set portraitUrl( value: string ) { this._portraitUrl = value; }
    public set score( value: number ) { this._score = value; }
    public set isBot( value: boolean ) { this._isBot = value; }
    public set nextPlayer( value: Player ) { this._nextPlayer = value; }
    public set isAway( value: boolean ) { this._isAway = value; }
    public set side( value: player_side_type ) { this._side = value; }
    public set isBusted( value: boolean ) { this._isBusted = value; }

    public get id(): number { return this._id; }
    public get name(): string { return this._name; }
    public get isBot(): boolean { return this._isBot; }
    public get nextPlayer(): Player { return this._nextPlayer; }
    public get isAway(): boolean { return this._isAway; }
    public get score(): number { return this._score; }
    public get side(): player_side_type { return this._side; }
    public get isBusted(): boolean { return this._isBusted; }


    public constructor()
    {
        super();
    }

    public init(): void
    {
        console.assert( this._id !== null, "Player.ts :: init() :: this._id cannot be null." );
        console.assert( this._name != null, "Player.ts :: init() :: this._name cannot be null." );
        console.assert( this._maxUsernameWidth != null, "Player.ts :: init() :: this._maxUsernameWidth cannot be null." );
        console.assert( this._portraitUrl != null, "Player.ts :: init() :: this._portraitUrl cannot be null." );
        console.assert( this._score !== null, "Player.ts :: init() :: this._score cannot be null." );
        console.assert( this._isBot != null, "Player.ts :: init() :: this._isBot cannot be null." );
          
        this._isAway = false;
        this._isBusted = false;

        let baseWrapper: PIXI.Container = new PIXI.Container();
        if ( this._side == player_side_type.RIGHT )
        {
            baseWrapper.scale.x = -1;
        }
        this.addChild( baseWrapper );
        //
        let base: PIXI.Sprite = new PIXI.Sprite( ServiceLocator.resources.hud_user_hud_base_slice_00.texture );
        base.scale.set( Math.max( this._maxUsernameWidth + 40, 120 ) / base.texture.width, 1 );
        base.x = 20;
        baseWrapper.addChild( base );
        //
        let baseEnd: PIXI.Sprite = new PIXI.Sprite( ServiceLocator.resources.hud_user_hud_base_slice_01.texture );
        baseEnd.x = base.x + base.width;
        baseWrapper.addChild( baseEnd );

        this._activePlayer = new PIXI.Sprite( ServiceLocator.resources.hud_active_player.texture );
        this._activePlayer.visible = false;
        this._activePlayer.scale.x = base.width / this._activePlayer.width;
        if ( this._side == player_side_type.RIGHT )
        {
            this._activePlayer.scale.x *= -1;
        }
        this._activePlayer.x = baseWrapper.scale.x == 1 ? base.x : -base.x;
        this._activePlayer.y = base.texture.height;
        this.addChild( this._activePlayer );

        this._portrait = new PIXI.Sprite( PIXI.Texture.from( this._portraitUrl ) );
        if ( this._portrait.texture.valid ) 
        {
            this._portrait.scale.set( this._kPortraitWidth/this._portrait.texture.width );
        } 
        else 
        {
            this._portrait.texture.baseTexture.on( "loaded", () => {
                this._portrait.scale.set( this._kPortraitWidth/this._portrait.texture.width ); } );
        }
        this._portrait.anchor.set( 0.5 );
        this._portrait.y = base.height * 0.5;
        this.addChild( this._portrait );

        let cooldownBase: PIXI.Sprite = new PIXI.Sprite( ServiceLocator.resources.hud_cooldown_base.texture );
        cooldownBase.anchor.set( 0.5 );
        cooldownBase.y = this._portrait.y;
        this.addChild( cooldownBase );

        this._cooldownBar = new PIXI.Sprite( ServiceLocator.resources.hud_cooldown_bar.texture );
        this._cooldownBar.visible = false;
        this._cooldownBar.anchor.set( 0.5 );
        this._cooldownBar.y = this._portrait.y;
        this.addChild( this._cooldownBar );

        this._radialMask = new RadialSpinner();
        this._radialMask.outerRadius = this._cooldownBar.texture.width * 0.5;
        this._radialMask.init();
        this._radialMask.position.copyFrom( this._cooldownBar.position );
        this.addChild( this._radialMask );
        //
        this._cooldownBar.mask = this._radialMask;

        this._nameText = new PIXI.Text( this._name, {
            fontSize: 30, 
            fontFamily: "teko_regular", 
            fill: 0x192f62, 
            stroke: 0xffffff, 
            strokeThickness: 4,
            dropShadow: true,
            dropShadowAngle: 2,
            dropShadowColor: "#ffffff",
            dropShadowDistance: 2,
            lineJoin: "round"
        } );
        this._nameText.x = this._side == player_side_type.LEFT ? base.x + 40 : -( base.x + 40 );
        this._nameText.y = 4;
        if ( this._side == player_side_type.RIGHT )
        {
            this._nameText.anchor.x = 1;
        }
        this.addChild( this._nameText );
        
        this._scoreText = new PIXI.Text( this._score.toString(), { fill: "white", fontSize: 60, fontFamily: "college_tm_regular" } );
        if ( this._side == player_side_type.RIGHT )
        {
            this._scoreText.anchor.x = 1;
        }
        this._scoreText.x = this._nameText.x;
        this._scoreText.y = 36;
        this.addChild( this._scoreText );
        
        this._timeOutBase = new PIXI.Sprite( ServiceLocator.resources.hud_time_left_base.texture );
        this._timeOutBase.visible = false;
        this._timeOutBase.anchor.set( 0.5 );
        this._timeOutBase.position.set( 72, 98 );
        if ( this._side == player_side_type.RIGHT )
        {
            this._timeOutBase.x *= -1;
        }
        this.addChild( this._timeOutBase );
        //
        this._timeOutText = new PIXI.Text( "", { fill: "white", fontSize: 36, fontFamily: "college_tm_regular" } );
        this._timeOutText.anchor.set( 0.5 );
        this._timeOutBase.addChild( this._timeOutText );
    }

    public end(): void
    {
        this._radialMask.end();
        this._radialMask = null;
    }

    public addScore( score: number ): void
    {
        this._score += score;
        this._scoreText.text = this._score.toString();
    }

    public setTimeOut( progress: number, remainingTime: number ): void
    {
        if ( progress == 0 )
        {
            this._cooldownBar.mask = null;
        }
        else
        {
            this._cooldownBar.mask = this._radialMask;
        }
        this._radialMask.setProgress( progress );

        this._cooldownBar.tint = progress > 0.5 ? 0xff0000 : 0xd0f400;

        this._timeOutText.text = remainingTime.toString() + "s";
    }

    public setHighlighted( isHighlighted: boolean ): void
    {
        //this._timeOutBase.visible = isHighlighted;
        this._cooldownBar.visible = isHighlighted;
        this._activePlayer.visible = isHighlighted;
        this._scoreText.style.fill = isHighlighted ? this._kActivePlayerScoreColor : 0xffffff;
    }

    public updateCharacter(): void
    {
        this._nameText.text = this._name;

        this._portrait.texture = PIXI.Texture.from( this._portraitUrl );
        if ( this._portrait.texture.valid ) 
        {
            this._portrait.scale.set( this._kPortraitWidth/this._portrait.texture.width );
        } 
        else 
        {
            this._portrait.texture.baseTexture.on( "loaded", () => {
                this._portrait.scale.set( this._kPortraitWidth/this._portrait.texture.width ); } );
        }
    }
}

export const enum player_side_type
{
    LEFT = 0,
    RIGHT,
}